import { ChatFolderData } from "@sms/shared/chat/chat-folder.interface";
import { eventChannel, EventChannel } from "redux-saga";
import { all, call, cancelled, Effect, fork, put, select, take } from "redux-saga/effects";
import { Socket } from "socket.io-client";
import { CurrentChatData } from "@sms/shared/chat/chat.interface";

import { socketChats } from "../chat.sockets";
import entryActionsChat from "../chat.entry-actions";

import {
  createChatFoldersAction,
  getChatFoldersAction,
  selectChangeFolderAction,
  selectChatChangeFoldersAction,
  selectDeleteFolderAction,
  setChatFoldersAction,
  setCurrentFolderAction,
  setSelectChatFoldersAction
} from "./folders.actions";

import { ApplicationStore } from "~/app/application.store";
import {
  setChatFolderModalIsOpen,
  setChatSelectFolderSettingModalIsOpen,
  setChatSelectFoldersModalIsOpen
} from "~/app/modals/modals.actions";

interface ActionSaga {
  type: string;
  payload: ChatFolderData | null | boolean | ChatFolderData[];
}

const createSocketChannelChatsFolders: any = (): any =>
  eventChannel(emit => {
    socketChats.emit("fetchChatsFolders");

    socketChats.on("chatFoldersUpdate", () => {
      socketChats.emit("fetchChatsFolders");
    });

    socketChats.on("recieveChatsFolders", (data: ChatFolderData[]) => {
      emit(data);
    });

    return (): void => {
      socketChats.off("chatFoldersUpdate");
      socketChats.off("recieveChatsFolders");
    };
  });

function* fetchChatsFolders(): Generator<
  Effect | Generator,
  void,
  ChatFolderData[] | EventChannel<any> | ChatFolderData | null
> {
  const socketChannelChatsFolders: EventChannel<any> = (yield call(
    createSocketChannelChatsFolders
  )) as EventChannel<any>;

  try {
    while (true) {
      const chatsFolders: ChatFolderData[] = (yield take(socketChannelChatsFolders)) as ChatFolderData[];

      yield put(setChatFoldersAction(chatsFolders));

      const currentChatFolder: ChatFolderData | null = (yield select(
        (state: ApplicationStore) => state.chatFolders.currentFolder
      )) as ChatFolderData | null;

      if (currentChatFolder) {
        // yield* chatsFolders.map(function* (chatsFolder: ChatFolderData) {
        //   if (chatsFolder.id === currentChatFolder.id) {
        //     yield put(setCurrentFolderAction(chatsFolder));
        //   }
        // });
      }
    }
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
      socketChannelChatsFolders.close();
    }
  } finally {
    if (yield cancelled()) {
      socketChannelChatsFolders.close();
    }
  }
}

export function* getChatsFoldersSaga(): Generator<Effect | Generator, void, void> {
  while (true) {
    yield take(getChatFoldersAction);
    yield fork(fetchChatsFolders);
  }
}

function* setCurrentFolderSaga(): Generator<Effect | Generator, void, string | null | ActionSaga | EventChannel<any>> {
  while (true) {
    const action: ActionSaga = (yield take(setCurrentFolderAction)) as ActionSaga;
    yield put(setCurrentFolderAction(action.payload as ChatFolderData | null));
  }
}

function* createChatFolder(): Generator<Effect | Generator, void, ChatFolderData> {
  const newChatFolderData: ChatFolderData = yield select(
    (state: ApplicationStore) => state.chatFolders.createChatFolder
  );

  try {
    socketChats.emit("createChatFolder", newChatFolderData);
    yield put(setChatFolderModalIsOpen(false));
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  }
}

export function* createChatFolderSaga(): Generator<Effect | Generator, void, void> {
  while (true) {
    yield take(createChatFoldersAction);
    yield fork(createChatFolder);
  }
}

function* changeChatFolder(): Generator<Effect | Generator, void, ChatFolderData> {
  const changeChatFolderData: ChatFolderData = yield select(
    (state: ApplicationStore) => state.chatFolders.changeChatFolder
  );

  try {
    socketChats.emit("changeChatFolder", changeChatFolderData);
    yield put(setChatSelectFolderSettingModalIsOpen(false));
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  }
}

export function* changeChatFolderSaga(): Generator<Effect | Generator, void, void> {
  while (true) {
    yield take(selectChangeFolderAction);
    yield fork(changeChatFolder);
  }
}

function* deleteChatFolder(chatFolder: ChatFolderData): Generator<Effect | Generator, void, void> {
  try {
    socketChats.emit("deleteChatFolder", chatFolder);
    yield put(setChatSelectFolderSettingModalIsOpen(false));
    yield put(setCurrentFolderAction(null));
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  }
}

export function* deleteChatFolderSaga(): Generator<Effect | Generator, void, ActionSaga> {
  while (true) {
    const action: ActionSaga = yield take(selectDeleteFolderAction);
    yield fork(deleteChatFolder, action.payload as ChatFolderData);
  }
}

const createSocketChannelSelectChatFolders: any = (chatId: string): any =>
  eventChannel(emit => {
    socketChats.emit("fetchSelectChatFolders", chatId);

    socketChats.on("recieveSelectChatFolders", (data: ChatFolderData[]) => {
      emit(data);
    });

    return (): void => {
      socketChats.off("recieveChatsFolders");
    };
  });

function* fetchSelectChatFolders(): Generator<
  Effect | Generator,
  void,
  CurrentChatData | null | EventChannel<any> | ChatFolderData[]
> {
  const selectChatId: string | null = (yield select((state: ApplicationStore) => state.chats.selectChatId)) as
    | string
    | null;
  const socketChannelSelectChatFolders: EventChannel<any> = (yield call(
    createSocketChannelSelectChatFolders,
    selectChatId
  )) as EventChannel<any>;

  try {
    if (selectChatId !== null) {
      while (true) {
        const selectChatFolders: ChatFolderData[] = (yield take(socketChannelSelectChatFolders)) as ChatFolderData[];
        yield put(setSelectChatFoldersAction(selectChatFolders));
      }
    }
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
      socketChannelSelectChatFolders.close();
    }
  } finally {
    if (yield cancelled()) {
      socketChannelSelectChatFolders.close();
    }
  }
}

export function* fetchSelectChatFoldersSaga(): Generator<Effect | Generator, void, ActionSaga> {
  while (true) {
    const action: ActionSaga = yield take(setChatSelectFoldersModalIsOpen);
    if (action.payload === true) {
      yield fork(fetchSelectChatFolders);
    }
  }
}

function* selectChatChangeFolders(
  folders: ChatFolderData[]
): Generator<Effect | Generator, void, void | ChatFolderData[] | CurrentChatData | null> {
  const selectChatId: string | null = (yield select((state: ApplicationStore) => state.chats.selectChatId)) as
    | string
    | null;
  try {
    socketChats.emit("selectChatChangeFolders", { folders: folders, chatId: selectChatId });
    yield put(setChatSelectFoldersModalIsOpen(false));
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  }
}

export function* selectChatChangeFoldersSaga(): Generator<Effect | Generator, void, ActionSaga> {
  while (true) {
    const action: ActionSaga = yield take(selectChatChangeFoldersAction);
    yield fork(selectChatChangeFolders, action.payload as ChatFolderData[]);
  }
}

export function* chatsFoldersSaga(): Generator<Effect | Generator, void, Socket<any>> {
  while (true) {
    yield take([entryActionsChat.handleConnectSocketChat]);

    yield all([
      fork(fetchChatsFolders),
      fork(setCurrentFolderSaga),
      fork(createChatFolderSaga),
      fork(fetchSelectChatFoldersSaga),
      fork(selectChatChangeFoldersSaga),
      fork(changeChatFolderSaga),
      fork(deleteChatFolderSaga)
    ]);
  }
}
